import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, ChevronLeft, ChevronRight, Calendar as CalendarIcon, TrendingUp, Minus, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  idea: "pill-gray",
  guion: "pill-blue",
  grabado: "pill-blue",
  editado: "pill-blue",
  diseno_listo: "pill-yellow",
  aprobado: "pill-green",
  programado: "pill-yellow",
  publicado: "pill-dark",
  medido: "pill-green",
};

const EditorialCalendar = () => {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState("month");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilters, setTypeFilters] = useState({ calendar: true, brief: true, script: true, copy: true, task: true });

  const TYPE_LABELS = { calendar: "Posts", brief: "Briefs", script: "Guiones", copy: "Copies", task: "Tareas" };
  const TYPE_COLORS = { calendar: "#D99D26", brief: "#8a5e10", script: "#0A0A0A", copy: "#666666", task: "#1f3f8a" };

  const load = () => {
    setLoading(true);
    const types = Object.entries(typeFilters).filter(([_, v]) => v).map(([k]) => k).join(",");
    Promise.all([
      api.get("/editorial/timeline", { params: { types } }),
      api.get("/campaigns"),
    ])
      .then(([cal, camps]) => {
        setItems(cal.data.items || []);
        setCampaigns(camps.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilters]);

  const toggleType = (t) => setTypeFilters((f) => ({ ...f, [t]: !f[t] }));

  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = monthEnd.getDate();
  const startDay = monthStart.getDay(); // 0=Sun
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getItemsForDay = (day) => {
    if (!day) return [];
    const ds = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split("T")[0];
    return items.filter((it) => (it.date || "").startsWith(ds));
  };

  const onDrop = async (e, day) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    await api.patch(`/calendar/${id}`, { date: newDate.toISOString().split("T")[0] });
    load();
  };

  return (
    <Layout
      title="Calendario Editorial"
      subtitle="El gran calendario de posts"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-calendar-btn">
          <Plus size={14} /> Nueva publicación
        </button>
      }
    >
      <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="btn-ghost p-2" data-testid="prev-month">
            <ChevronLeft size={16} />
          </button>
          <div className="font-display text-2xl font-medium" data-testid="current-month">
            {currentMonth.toLocaleDateString("es-MX", { month: "long", year: "numeric" })}
          </div>
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="btn-ghost p-2" data-testid="next-month">
            <ChevronRight size={16} />
          </button>
          <button onClick={() => setCurrentMonth(new Date())} className="btn-ghost">Hoy</button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("month")} className={`pill cursor-pointer ${view === "month" ? "pill-yellow" : "pill-gray"}`}>Mes</button>
          <button onClick={() => setView("list")} className={`pill cursor-pointer ${view === "list" ? "pill-yellow" : "pill-gray"}`}>Lista</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : view === "month" ? (
        <div className="aura-card-flat overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[#E5E5E5]">
            {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
              <div key={d} className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[#666]">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {days.map((d, i) => (
              <div
                key={i}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, d)}
                className={`min-h-28 p-2 border-r border-b border-[#F0F0F0] ${d ? "bg-white" : "bg-[#FAFAFA]"} cursor-pointer hover:bg-[#FFF9E6]/30 transition-colors`}
                onClick={() => {
                  if (d) {
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d).toISOString().split("T")[0];
                    setEditing({ date });
                    setShowForm(true);
                  }
                }}
              >
                {d && (
                  <>
                    <div className="text-xs font-medium text-[#666] mb-1">{d}</div>
                    <div className="space-y-1">
                      {getItemsForDay(d).slice(0, 3).map((it) => (
                        <div
                          key={`${it.type}-${it.id}`}
                          draggable={it.type === "calendar"}
                          onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("text/plain", it.id); }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (it.type === "calendar") {
                              setEditing(it.raw);
                              setShowForm(true);
                            }
                          }}
                          className="text-[11px] rounded px-1.5 py-0.5 truncate cursor-pointer hover:opacity-80 flex items-center gap-1"
                          style={{ background: TYPE_COLORS[it.type], color: it.type === "calendar" || it.type === "brief" ? "#0A0A0A" : "#fff" }}
                          data-testid={`calendar-item-${it.id}`}
                          title={`${TYPE_LABELS[it.type]} · ${it.status}`}
                        >
                          {it.time && <span className="font-medium">{it.time} </span>}
                          {it.title}
                        </div>
                      ))}
                      {getItemsForDay(d).length > 3 && (
                        <div className="text-[10px] text-[#666]">+{getItemsForDay(d).length - 3} más</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="aura-card-flat p-2">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-wider text-[#666]">
                <th className="p-3">Fecha</th>
                <th className="p-3">Título</th>
                <th className="p-3">Canal</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Creador</th>
              </tr>
            </thead>
            <tbody>
              {items.sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((it) => (
                <tr key={`${it.type}-${it.id}`} onClick={() => { if (it.type === "calendar") { setEditing(it.raw); setShowForm(true); } }} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] cursor-pointer">
                  <td className="p-3 text-sm">{(it.date || "").substring(0, 10)} {it.time && <span className="text-[#666]">· {it.time}</span>}</td>
                  <td className="p-3 text-sm font-medium flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: TYPE_COLORS[it.type] }} />
                    {it.title}
                  </td>
                  <td className="p-3 text-sm">{it.platform || TYPE_LABELS[it.type]}</td>
                  <td className="p-3"><span className={`pill ${STATUS_COLORS[it.status] || "pill-gray"} text-[10px]`}>{it.status}</span></td>
                  <td className="p-3 text-sm text-[#666]">{it.creator_name || "—"}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="5" className="p-10 text-center text-[#666]">Sin publicaciones</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CalendarItemForm
          initial={editing}
          campaigns={campaigns}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </Layout>
  );
};

const CalendarItemForm = ({ initial, campaigns, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: initial?.title || "",
    date: initial?.date || new Date().toISOString().split("T")[0],
    time: initial?.time || "12:00",
    platform: initial?.platform || "instagram",
    account: initial?.account || "@rubiperfumeria",
    format: initial?.format || "post",
    creator_name: initial?.creator_name || "",
    campaign_id: initial?.campaign_id || "",
    status: initial?.status || "idea",
    notes: initial?.notes || "",
    post_link: initial?.post_link || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const campaign = campaigns.find((c) => c.id === form.campaign_id);
      const body = {
        ...form,
        campaign_name: campaign?.name || null,
        campaign_color: campaign?.color || "#D99D26",
      };
      if (initial?.id) {
        await api.patch(`/calendar/${initial.id}`, body);
        toast.success("Publicación actualizada");
      } else {
        await api.post("/calendar", body);
        toast.success("Publicación agendada");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!initial?.id || !window.confirm("¿Eliminar?")) return;
    await api.delete(`/calendar/${initial.id}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-7 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">{initial?.id ? "Editar" : "Crear"}</div>
            <h2 className="font-display text-2xl font-medium">{initial?.id ? "Editar publicación" : "Nueva publicación"}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="aura-label">Título *</label>
          <input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cal-title" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="aura-label">Fecha</label>
            <input type="date" className="aura-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Hora</label>
            <input type="time" className="aura-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Plataforma</label>
            <select className="aura-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              {["instagram", "tiktok", "facebook", "youtube_shorts", "whatsapp", "tiktok_shop"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Formato</label>
            <select className="aura-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
              {["post", "reel", "story", "carrusel", "video", "banner", "live"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Creador</label>
            <input className="aura-input" value={form.creator_name} onChange={(e) => setForm({ ...form, creator_name: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Campaña</label>
            <select className="aura-input" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
              <option value="">— Sin campaña —</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="aura-label">Estado</label>
            <div className="flex gap-1 flex-wrap">
              {Object.keys(STATUS_COLORS).map((s) => (
                <button key={s} type="button" onClick={() => setForm({ ...form, status: s })} className={`pill cursor-pointer text-[10px] ${form.status === s ? "pill-yellow" : "pill-gray"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <label className="aura-label">Link publicado (cuando se haya posteado)</label>
            <input className="aura-input" value={form.post_link} onChange={(e) => setForm({ ...form, post_link: e.target.value })} placeholder="https://..." />
          </div>
          <div className="col-span-2">
            <label className="aura-label">Notas</label>
            <textarea className="aura-input" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        {initial?.id && (initial.status === "publicado" || form.status === "publicado" || initial.performance_score) && (
          <PerformanceMarker item={initial} onChange={onSaved} />
        )}

        <div className="flex justify-between gap-2 pt-2">
          {initial?.id ? (
            <button type="button" onClick={remove} className="btn-ghost text-red-600"><Trash2 size={14} className="inline mr-1" /> Eliminar</button>
          ) : <div />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="cal-submit">
              {saving && <span className="spinner" />} {initial?.id ? "Guardar" : "Crear"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const PerformanceMarker = ({ item, onChange }) => {
  const [score, setScore] = useState(item?.performance_score || item?.raw?.performance_score || "");
  const [notes, setNotes] = useState(item?.performance_notes || item?.raw?.performance_notes || "");
  const [reach, setReach] = useState(item?.performance_metrics?.reach || item?.raw?.performance_metrics?.reach || "");
  const [engagement, setEngagement] = useState(item?.performance_metrics?.engagement || item?.raw?.performance_metrics?.engagement || "");
  const [saving, setSaving] = useState(false);

  const save = async (chosenScore) => {
    const finalScore = chosenScore || score;
    if (!finalScore) { toast.error("Selecciona viral, normal o no funcionó"); return; }
    setSaving(true);
    try {
      const id = item.raw?.id || item.id;
      await api.post(`/marketing/calendar/${id}/performance`, {
        performance_score: finalScore,
        performance_notes: notes,
        metrics: { reach: Number(reach) || 0, engagement: Number(engagement) || 0 },
      });
      setScore(finalScore);
      toast.success("Resultado registrado · Aura lo usará para mejorar próximas propuestas");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const OPTIONS = [
    { id: "viral", label: "🔥 Viral / éxito alto", color: "bg-green-50 border-green-300 text-green-800", icon: TrendingUp },
    { id: "normal", label: "➖ Normal / esperado", color: "bg-gray-50 border-gray-300 text-gray-700", icon: Minus },
    { id: "no_funciono", label: "❌ No funcionó", color: "bg-red-50 border-red-300 text-red-700", icon: TrendingDown },
  ];

  return (
    <div className="border-t-2 border-dashed border-[#D99D26]/30 pt-4 mt-4" data-testid="performance-marker">
      <div className="label-overline mb-3">🧠 Aprendizaje para Aura — ¿Cómo le fue a este post?</div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {OPTIONS.map((o) => {
          const Icon = o.icon;
          const active = score === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => save(o.id)}
              disabled={saving}
              className={`p-3 border-2 rounded-xl text-xs font-medium transition-all ${active ? o.color + " ring-2 ring-offset-1 ring-[#D99D26]" : "bg-white border-[#E5E5E5] hover:border-[#D99D26]"}`}
              data-testid={`perf-${o.id}`}
            >
              <Icon size={16} className="mx-auto mb-1" />
              {o.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="aura-label text-[10px]">Alcance (opcional)</label>
          <input type="number" className="aura-input text-xs" value={reach} onChange={(e) => setReach(e.target.value)} placeholder="12500" />
        </div>
        <div>
          <label className="aura-label text-[10px]">Engagement % (opcional)</label>
          <input type="number" className="aura-input text-xs" value={engagement} onChange={(e) => setEngagement(e.target.value)} placeholder="4.8" />
        </div>
      </div>
      <textarea
        className="aura-input text-xs"
        rows="2"
        placeholder="Por qué crees que funcionó o no... (Aura aprende de estas notas)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="perf-notes"
      />
      {score && (
        <button type="button" onClick={() => save(score)} disabled={saving} className="btn-outline text-xs mt-2 w-full justify-center" data-testid="perf-save-notes">
          {saving ? <span className="spinner inline-block" /> : "Guardar métricas y notas"}
        </button>
      )}
    </div>
  );
};

export default EditorialCalendar;
